import { useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { usePageStyles } from "../../../shared/styles";
import type { StatConfig } from "../types";

const iconStyle = { width: 24, height: 24, fill: "none" as const, strokeWidth: 2 };

function renderStatIcon(icon: string, color: string) {
    const style = { ...iconStyle, stroke: color };
    switch (icon) {
        case "send":
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case "progress":
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" strokeLinecap="round" />
                </svg>
            );
        case "check":
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case "warning":
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4m0 4h.01" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        default:
            return null;
    }
}

export interface StatsGridProps {
    statusCounts: StatConfig[];
    listUrl: string;
    hoveredStat: string | null;
    onHoverStat: (key: string | null) => void;
}

export function StatsGrid({ statusCounts, listUrl, hoveredStat, onHoverStat }: StatsGridProps) {
    const navigate = useNavigate();
    const styles = usePageStyles();

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-7">
            {statusCounts.map((stat) => (
                <div
                    key={stat.key}
                    onMouseEnter={() => onHoverStat(stat.key)}
                    onMouseLeave={() => onHoverStat(null)}
                    className="rounded-[20px] p-6 relative overflow-hidden cursor-pointer transition-all duration-300"
                    style={{
                        background: styles.card.background,
                        border: `1px solid ${hoveredStat === stat.key ? `${stat.color}40` : styles.card.border}`,
                        transform: hoveredStat === stat.key ? "translateY(-4px)" : "none",
                        boxShadow: hoveredStat === stat.key ? `0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px ${stat.color}20` : styles.card.boxShadow,
                    }}
                    onClick={() => navigate(`${listUrl}?status=${stat.key}`)}
                >
                    <div
                        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                        style={{
                            background: `radial-gradient(circle at 30% 0%, ${stat.color}15 0%, transparent 60%)`,
                            opacity: hoveredStat === stat.key ? 1 : 0,
                        }}
                    />
                    <div className="flex justify-between items-start relative">
                        <div>
                            <p
                                className="mb-2 text-[13px] font-medium"
                                style={{
                                    color: styles.container.color === "#e8e8ed" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                                }}
                            >
                                {stat.label}
                            </p>
                            <p className="m-0 text-4xl font-bold leading-none tracking-tight" style={{ color: styles.container.color }}>
                                {stat.count ?? 0}
                            </p>
                            <p
                                className="mt-2 text-xs"
                                style={{
                                    color: styles.container.color === "#e8e8ed" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                }}
                            >
                                {stat.description}
                            </p>
                        </div>
                        <div
                            className="w-[52px] h-[52px] rounded-2xl flex items-center justify-center relative"
                            style={{ background: `${stat.color}1a` }}
                        >
                            {stat.pulse && (
                                <div
                                    className="absolute inset-0 rounded-2xl animate-pulse"
                                    style={{ background: `${stat.color}1a` }}
                                />
                            )}
                            <div className="relative">{renderStatIcon(stat.icon, stat.color)}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
